import React from 'react';
import { Rule } from '../types';
import { Trash2, Edit, Download, Power, PowerOff, Copy } from 'lucide-react';
import { exportRuleToJson } from '../utils/jsonLogicConverter';

interface RuleListProps {
  rules: Rule[];
  onEdit: (rule: Rule) => void;
  onDelete: (id: string) => void;
  onToggle: (id: string) => void;
  onDuplicate: (rule: Rule) => void;
}

const RuleList: React.FC<RuleListProps> = ({ rules, onEdit, onDelete, onToggle, onDuplicate }) => {
  const handleExport = (rule: Rule) => {
    const jsonRule = exportRuleToJson(rule);
    const dataStr = JSON.stringify(jsonRule, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `rule-${rule.name.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (rules.length === 0) {
    return (
      <div className="empty-state">
        <h3>No Rules Created</h3>
        <p>Click "Create New Rule" to get started</p>
      </div>
    );
  }

  return (
    <div className="rule-list">
      <table>
        <thead>
          <tr>
            <th>Status</th>
            <th>Name</th>
            <th>Description</th>
            <th>Notification</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rules.map(rule => (
            <tr key={rule.id} className={!rule.active ? 'inactive' : ''}>
              <td>
                <button
                  onClick={() => onToggle(rule.id)}
                  className={`status-toggle ${rule.active ? 'active' : 'inactive'}`}
                  title={rule.active ? 'Active' : 'Inactive'}
                >
                  {rule.active ? <Power size={16} /> : <PowerOff size={16} />}
                </button>
              </td>
              <td className="rule-name">{rule.name}</td>
              <td className="rule-description">{rule.description || '-'}</td>
              <td className="notification-preview">
                <div className="notification-topic">{rule.notification.topic}</div>
                <div className="notification-body">{rule.notification.body.substring(0, 50)}...</div>
              </td>
              <td className="date">{formatDate(rule.createdAt)}</td>
              <td className="actions">
                <button
                  onClick={() => onEdit(rule)}
                  className="btn-icon"
                  title="Edit Rule"
                >
                  <Edit size={16} />
                </button>
                <button
                  onClick={() => onDuplicate(rule)}
                  className="btn-icon"
                  title="Duplicate Rule"
                >
                  <Copy size={16} />
                </button>
                <button
                  onClick={() => handleExport(rule)}
                  className="btn-icon"
                  title="Export as JSON"
                >
                  <Download size={16} />
                </button>
                <button
                  onClick={() => {
                    if (window.confirm(`Are you sure you want to delete "${rule.name}"?`)) {
                      onDelete(rule.id);
                    }
                  }}
                  className="btn-icon btn-danger"
                  title="Delete Rule"
                >
                  <Trash2 size={16} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default RuleList;